import { useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';
import { get } from 'lodash';

import { useTranslation } from '@crehana/i18n';
import { useIsClient } from '@crehana/react-hooks';
import { TextField } from '@crehana/ui';

import {
  B2BLoginSocialButtons,
  Divider,
  EmailInput,
  FormSubmitButton,
} from '@/views/Auth/components';
import { validations } from '@/views/Auth/constants';
import AUTH_PAGES_LOGIN_USER_MUTATION from '@/views/Auth/graphql/AuthPagesLoginUserMutation.auth.graphql';
import {
  AuthPagesLoginUserMutation,
  AuthPagesLoginUserMutationVariables,
} from '@/views/Auth/graphql/types/AuthPagesLoginUserMutation';
import routes from '@/views/Auth/routes';
import { getEmailParam, getNextUrl } from '@/views/Auth/utils';

type TFormValues = {
  email: string;
  password: string;
};
const LoginForm = () => {
  const { t } = useTranslation();
  const isClient = useIsClient();
  const nextUrl = getNextUrl();
  const defaultEmail = getEmailParam() as string;
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<TFormValues>({
    defaultValues: {
      email: defaultEmail,
      password: '',
    },
  });

  const [loginUser] = useMutation<
    AuthPagesLoginUserMutation,
    AuthPagesLoginUserMutationVariables
  >(AUTH_PAGES_LOGIN_USER_MUTATION, {
    context: { clientName: 'auth' },
    onCompleted: data => {
      if (data?.loginUser?.success) {
        window.location.replace(data?.loginUser?.redirect || '');
      } else {
        setError('email', {
          type: 'manual',
          message: get(data?.loginUser?.messages, '[0].message'),
        });
      }
    },
  });

  const onSubmit = (data: TFormValues) =>
    loginUser({
      variables: {
        username: data.email,
        password: data.password,
        nextUrl,
      },
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center">
        <h3 className="font-h3 text-base-dark">{t('FORM_TITLE')}</h3>
        <p className="font-body4 mb-24 pl-32 pr-32 sm:pl-0 sm:pr-0 md:pl-0 md:pr-0 lg:pl-0 lg:pr-0  text-base-dark">
          {t('FORM_DESCRIPTION')}
        </p>
      </div>
      {isClient && (
        <B2BLoginSocialButtons className="mb-24 font-subtitle3 text-base-main" />
      )}
      <Divider className="pb-24 font-body4 text-base-main" />
      <EmailInput
        className="mb-24"
        id="email"
        name="email"
        label={t('INPUT_EMAIL_LABEL')}
        hint={t('INPUT_EMAIL_HINT')}
        ref={register({
          required: validations.required(t),
          pattern: validations.email(t),
        })}
        defaultValue={defaultEmail}
        disabled={isSubmitting}
        errorText={errors.email?.message}
        full
        hideB2CMsg
        hideB2BLoginMsg
      />
      <TextField
        className="mb-8"
        id="password"
        name="password"
        type="password"
        label={t('INPUT_PASSWORD_LABEL')}
        hint={t('INPUT_PASSWORD_HINT')}
        errorText={errors.password?.message}
        ref={register({
          required: validations.required(t),
          minLength: validations.minLength(t),
          maxLength: validations.maxLength(t),
        })}
        disabled={isSubmitting}
        full
      />
      <a
        href={routes.b2bRecoverPasswordPath}
        className="block font-caption text-primary-main mb-24"
      >
        {t('FORGOT_PASSWORD')}
      </a>
      <FormSubmitButton
        label={t(isSubmitting ? 'SENDING' : 'BUTTON_SUBMIT_LABEL')}
        disabled={isSubmitting}
      />
    </form>
  );
};

export default LoginForm;
